import React from 'react';
import { StanButton } from '@/components';
import { SUBSCRIPTION_STATUS } from '@/lib/api/subscriptions/types';
import { SubscriptionSection } from './billing/SubscriptionSection';
import { UpdatePaymentMethodSection } from './billing/UpdatePaymentMethodSection';
import { InvoicesSection } from './billing/InvoicesSection';
import { CancellationFlowOrchestrator } from './cancellation';
import { ChangePlanModal } from '@/features/subscriptions/components/ChangePlanModal';
import { useBillingTab } from '../hooks/useBillingTab';

export const BillingTab: React.FC = () => {
    const {
        // State
        loading,
        isCancellationFlowOpen,
        isChangePlanModalOpen,
        customerId,

        // Subscription info
        platformSubscription,
        platformSubscriptionId,
        currentSubscriptionStatus,
        currentBilledPlan,
        billingCycle,
        nextBillDate,
        pricingDetail,

        // Subscription type flags
        isAffiliateOnlySubscription,
        isMobileSubscription,

        // Trial info
        isNoExtraCostTrial,
        trialEndDate,
        trialExpirationMessage,

        // Discount info
        activeCouponName,
        isFreeForever,

        // Offer qualification
        shouldShowOffer,
        hasTakenFreeMonth,

        // Free trial state
        isInFreeTrial,

        // Actions
        handleCancelSubscription,
        handleCancellationFlowComplete,
        handleChangePlan,
        handleRenewSubscription,
        closeChangePlanModal,
    } = useBillingTab();

    return (
        <>
            <div className='space-y-8'>
                {/* Subscription and Payment Details Row */}
                <div className='grid w-full max-w-5xl grid-cols-1 gap-6 lg:grid-cols-2 xl:max-w-7xl'>
                    {/* Subscription Details */}
                    <SubscriptionSection
                        currentSubscriptionStatus={currentSubscriptionStatus}
                        currentBilledPlan={currentBilledPlan}
                        pricingDetail={pricingDetail}
                        billingCycle={billingCycle}
                        nextBillDate={nextBillDate}
                        platformSubscription={platformSubscription}
                        loading={loading}
                        isAffiliateOnly={isAffiliateOnlySubscription}
                        isNoExtraCostTrial={isNoExtraCostTrial}
                        trialEndDate={trialEndDate ?? undefined}
                        trialExpirationMessage={trialExpirationMessage}
                        activeCouponName={activeCouponName ?? undefined}
                        isFreeForever={isFreeForever}
                        isInFreeTrial={isInFreeTrial}
                        onCancelSubscription={handleCancelSubscription}
                        onChangePlan={handleChangePlan}
                        onRenewSubscription={handleRenewSubscription}
                    />

                    {/* Payment Details */}
                    {!isAffiliateOnlySubscription && customerId && (
                        <UpdatePaymentMethodSection
                            isMobileSubscription={isMobileSubscription}
                            customerId={customerId}
                        />
                    )}
                </div>

                {/* Invoices Table */}
                {!isAffiliateOnlySubscription && <InvoicesSection />}

                {/* Affiliate Upgrade - Only show if no active subscription */}
                {isAffiliateOnlySubscription && currentSubscriptionStatus < SUBSCRIPTION_STATUS.CANCELLED && (
                    <div className='relative rounded-xl p-6'>
                        {/* Blur/Frost overlay */}
                        <div className='absolute inset-0 z-10 flex flex-col items-center justify-center rounded-xl backdrop-blur-xs'>
                            <h3 className='text-heading-h3 mb-4 text-center'>Upgrade Your Account</h3>
                            <p className='mb-6 text-center'>Start your 14-day free trial and unlock all features.</p>
                            <StanButton name='start-free-trial-button' size='lg' onClick={handleChangePlan}>
                                Start My 14 Day Free Trial
                            </StanButton>
                        </div>
                    </div>
                )}
            </div>

            {/* Cancellation Flow Orchestrator */}
            <CancellationFlowOrchestrator
                isOpen={isCancellationFlowOpen}
                platformSubscriptionId={platformSubscriptionId}
                hasTakenFreeMonth={hasTakenFreeMonth}
                shouldShowOffer={shouldShowOffer}
                onComplete={handleCancellationFlowComplete}
            />

            {/* Change Plan Modal */}
            <ChangePlanModal isOpen={isChangePlanModalOpen} onClose={closeChangePlanModal} />
        </>
    );
};

export default BillingTab;
